import { useEffect, useState } from "react";
import { Save, RotateCcw, Plus, ArrowUp, ArrowDown, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import AdminHeader from "@/components/AdminHeader";
import { normalizeSiteContent, clearSiteContentCache } from "@/pages/site/siteContent";
import SectionEditor, { SECTION_TYPES, newSection } from "@/pages/website/SectionEditor";
import BlogManager from "@/pages/website/BlogManager";
import { inputCls, areaCls, labelCls, Field, ImageField, StringListEditor } from "@/pages/website/fields";
import { ICONS } from "@/pages/site/SiteSections";

function VisitorsCard() {
  const [stats, setStats] = useState(null);
  const [excluded, setExcluded] = useState(() => { try { return localStorage.getItem("wems_no_track") === "1"; } catch { return false; } });
  useEffect(() => {
    api.get("/site/stats").then(({ data }) => setStats(data.days || [])).catch(() => setStats([]));
  }, []);
  const excludeMe = async () => {
    try {
      const vid = localStorage.getItem("wems_vid") || "";
      localStorage.setItem("wems_no_track", "1");
      if (vid) await api.post("/site/stats/exclude-me", { vid, path: "" });
      setExcluded(true);
      toast.success("This device won't be counted any more");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  if (!stats) return null;
  const sum = (rows, k) => rows.reduce((a, d) => a + (d[k] || 0), 0);
  const today = stats[0] || { views: 0, visitors: 0 };
  const topPages = Object.entries(
    stats.reduce((acc, d) => { for (const [p, n] of Object.entries(d.paths || {})) acc[p] = (acc[p] || 0) + n; return acc; }, {})
  ).sort((a, b) => b[1] - a[1]).slice(0, 8);
  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="cms-visitors">
      <h2 className="font-heading text-lg font-bold text-[#0F2547]">Website visitors</h2>
      <p className="text-xs text-slate-500">Counted automatically as people move around the site. No cookies — visitors stay anonymous.</p>
      <div className="grid grid-cols-3 gap-3">
        {[
          ["Today", today.views, today.visitors],
          ["Last 7 days", sum(stats.slice(0, 7), "views"), sum(stats.slice(0, 7), "visitors")],
          ["Last 30 days", sum(stats, "views"), sum(stats, "visitors")],
        ].map(([label, views, visitors], i) => (
          <div key={label} className="rounded-xl border border-slate-200 p-3 text-center" data-testid={`cms-visitors-stat-${i}`}>
            <p className="text-2xl font-extrabold text-[#0F2547]">{views}</p>
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{label} · {visitors} visitors</p>
          </div>
        ))}
      </div>
      {topPages.length > 0 && (
        <div className="pt-1" data-testid="cms-visitors-pages">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Most viewed pages (30 days)</p>
          {topPages.map(([p, n]) => (
            <div key={p} className="flex justify-between text-sm py-1 border-b border-slate-100 last:border-0">
              <span className="font-semibold text-slate-700">{p === "/" ? "Home" : p}</span>
              <span className="text-slate-500">{n} views</span>
            </div>
          ))}
        </div>
      )}
      <button onClick={excludeMe} disabled={excluded} data-testid="cms-visitors-exclude-me"
        className="h-9 px-4 rounded-lg border border-slate-300 text-slate-600 hover:border-[#D5281B] hover:text-[#D5281B] text-xs font-bold disabled:opacity-50 bg-white transition-colors">
        {excluded ? "This device is excluded from the stats" : "Stop counting my visits on this device"}
      </button>
    </section>
  );
}

const TABS = [["pages", "Pages"], ["services", "Services & calculator"], ["blog", "Blog"], ["settings", "Settings"]];

const EDITABLE_PAGES = [
  ["home", "Home"],
  ["services", "Services"],
  ["service-medical", "Medical Event Cover page"],
  ["service-sports", "Sports Event Cover page"],
  ["about", "About Us"],
  ["contact", "Contact Us"],
  ["offers", "What We Offer page"],
  ["trusted", "People We Trust page"],
  ["gallery", "Gallery page"],
];

const PAGE_LABELS = { services: "Services", about: "About Us", calculator: "Staff Calculator", contact: "Contact Us", gallery: "Gallery", trusted: "People We Trust" };

const CALC_FIELDS = [
  ["hero_title", "Banner title"], ["hero_subtitle", "Banner subtitle"],
  ["intro_title", "Calculator heading"], ["intro_text", "Intro text", true],
  ["disclaimer", "Disclaimer line", true],
  ["tiers_title", "Tiers table heading"], ["tiers_text", "Tiers table intro"],
  ["table_title", "Score table heading"], ["martyns_title", "Martyn's Law heading"],
];

const SOCIAL_PLATFORMS = [["facebook", "Facebook"], ["instagram", "Instagram"], ["twitter", "X (Twitter)"], ["linkedin", "LinkedIn"]];

function SeoCard() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/site/content").then(({ data }) => {
      const seo = data.seo || {};
      setForm({
        title: seo.title || "Event Medical Cover UK | Wales Event Medical Services",
        description: seo.description || "Professional event medical cover across the UK — first responders to doctors, ambulances and treatment units for festivals, sports, corporate and community events. Free, no-obligation quotes.",
        keywords: seo.keywords || "event medical cover, event first aid cover, medical cover for events UK, event medics Wales, sports event medical cover, festival medical cover",
      });
    }).catch(() => setForm({ title: "", description: "", keywords: "" }));
  }, []);
  if (!form) return null;
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/site/content", { seo: form });
      clearSiteContentCache();
      toast.success("SEO saved — live on the website");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };
  const cls = "w-full h-11 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]";
  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="cms-seo">
      <h2 className="font-heading text-lg font-bold text-[#0F2547]">SEO — how you appear on Google</h2>
      <p className="text-xs text-slate-500">The site-wide title, description and keywords Google uses. Each page also has its own title and description — these are the defaults and the keyword list.</p>
      <label className="block">
        <span className="text-xs font-semibold text-slate-500 block mb-1">Site title (shown in the Google listing &amp; browser tab)</span>
        <input className={cls} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="cms-seo-title" />
      </label>
      <label className="block">
        <span className="text-xs font-semibold text-slate-500 block mb-1">Description (the two lines under your Google listing)</span>
        <textarea rows={3} className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="cms-seo-description" />
      </label>
      <label className="block">
        <span className="text-xs font-semibold text-slate-500 block mb-1">Keywords (comma-separated)</span>
        <textarea rows={2} className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]" value={form.keywords} onChange={(e) => setForm((f) => ({ ...f, keywords: e.target.value }))} data-testid="cms-seo-keywords" />
      </label>
      <button onClick={save} disabled={saving} data-testid="cms-seo-save"
        className="h-11 px-6 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-sm font-bold disabled:opacity-60">
        {saving ? "Saving…" : "Save SEO"}
      </button>
    </section>
  );
}

function SocialCard() {
  const [state, setState] = useState(null);
  const [form, setForm] = useState({ ayrshare_api_key: "", x_api_key: "", x_api_secret: "", auto_share: true });
  const [saving, setSaving] = useState(false);
  const [profiles, setProfiles] = useState(null);
  const [checking, setChecking] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/site/social");
      setState(data);
      setForm((f) => ({ ...f, auto_share: data.auto_share !== false }));
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { auto_share: form.auto_share };
      if (form.ayrshare_api_key.trim()) payload.ayrshare_api_key = form.ayrshare_api_key.trim();
      if (form.x_api_key.trim()) payload.x_api_key = form.x_api_key.trim();
      if (form.x_api_secret.trim()) payload.x_api_secret = form.x_api_secret.trim();
      const { data } = await api.put("/site/social", payload);
      setState(data);
      setForm((f) => ({ ...f, ayrshare_api_key: "", x_api_key: "", x_api_secret: "" }));
      setProfiles(null);
      toast.success("Social settings saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const check = async () => {
    setChecking(true);
    try {
      const { data } = await api.get("/site/social/profiles");
      setProfiles(data);
      if (data.error) toast.error(data.error);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setChecking(false);
    }
  };

  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="cms-social">
      <h2 className="font-heading text-lg font-bold text-[#0F2547]">Social media auto-posting</h2>
      <div className="text-xs text-slate-500 space-y-1.5 leading-relaxed">
        <p>When a blog post goes live, it's shared automatically to your social pages with a link back to the post. You can also share any published post by hand from the Blog tab.</p>
        <p className="font-semibold text-slate-600">One-time setup (about 10 minutes):</p>
        <ol className="list-decimal pl-4 space-y-1">
          <li>Create an account at <span className="font-semibold">ayrshare.com</span> — a service that posts to all your social pages at once.</li>
          <li>Inside Ayrshare, connect your Facebook Page, Instagram Business account, X and LinkedIn.</li>
          <li>Copy your API key from the Ayrshare dashboard and paste it below.</li>
          <li>For X (Twitter) only: Ayrshare also needs an X developer app's API key &amp; secret — paste those too. Without them, posts go to Facebook, Instagram and LinkedIn only.</li>
        </ol>
      </div>
      <p className="text-xs font-semibold" data-testid="cms-social-status">
        {state?.configured ? <span className="text-emerald-600">Connected — auto-posting is ready</span> : <span className="text-amber-600">Not set up yet — posts won't be shared until you add a key</span>}
      </p>
      <Field label="Ayrshare API key">
        <input type="password" className={inputCls} value={form.ayrshare_api_key} placeholder={state?.configured ? "Saved — paste a new key to replace it" : "Paste your Ayrshare API key"}
          onChange={(e) => setForm((f) => ({ ...f, ayrshare_api_key: e.target.value }))} data-testid="cms-social-key" />
      </Field>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="X API key (only for X/Twitter)">
          <input type="password" className={inputCls} value={form.x_api_key} placeholder={state?.x_configured ? "Saved" : "Optional"}
            onChange={(e) => setForm((f) => ({ ...f, x_api_key: e.target.value }))} data-testid="cms-social-xkey" />
        </Field>
        <Field label="X API secret (only for X/Twitter)">
          <input type="password" className={inputCls} value={form.x_api_secret} placeholder={state?.x_configured ? "Saved" : "Optional"}
            onChange={(e) => setForm((f) => ({ ...f, x_api_secret: e.target.value }))} data-testid="cms-social-xsecret" />
        </Field>
      </div>
      <label className="flex items-center gap-2.5 h-11 px-3 rounded-lg border border-slate-200 hover:border-slate-300 cursor-pointer" data-testid="cms-social-auto">
        <input type="checkbox" checked={form.auto_share} onChange={(e) => setForm((f) => ({ ...f, auto_share: e.target.checked }))} className="h-4 w-4 accent-[#005EB8]" />
        <span className="text-sm font-semibold text-slate-700">Share automatically when a post goes live</span>
      </label>
      <div className="flex gap-2 flex-wrap">
        <button onClick={save} disabled={saving} data-testid="cms-social-save"
          className="h-11 px-4 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-50 text-white text-sm font-bold">
          {saving ? "Saving…" : "Save social settings"}
        </button>
        <button onClick={check} disabled={checking || !state?.configured} data-testid="cms-social-check"
          className="h-11 px-4 rounded-lg border border-slate-300 text-[#005EB8] hover:border-[#005EB8] disabled:opacity-40 text-sm font-bold">
          {checking ? "Checking…" : "Check connections"}
        </button>
      </div>
      {profiles && (
        <div className="flex flex-wrap gap-2" data-testid="cms-social-badges">
          {SOCIAL_PLATFORMS.map(([k, label]) => (
            <span key={k} data-testid={`cms-social-badge-${k}`}
              className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${profiles.connected?.includes(k) ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
              {label}: {profiles.connected?.includes(k) ? "connected" : "not connected"}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

export default function AdminWebsite() {
  const [tab, setTab] = useState("pages");
  const [page, setPage] = useState(() => new URLSearchParams(window.location.search).get("page") || "home");
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [addType, setAddType] = useState("text");

  const load = async () => {
    try {
      const { data } = await api.get("/site/content");
      setC(normalizeSiteContent(data));
    } catch {
      setC(normalizeSiteContent({}));
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/site/content", {
        page_sections: c.page_sections,
        services_catalog: c.services_catalog,
        calculator_texts: c.calculator_texts,
        contact_info: c.contact_info,
        blog_settings: c.blog_settings,
        pages: c.pages,
        nav_links: c.nav_links,
        header: c.header,
        footer: c.footer,
        page_meta: c.page_meta,
      });
      clearSiteContentCache();
      toast.success("Website updated — it's live on the front page");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const sections = c?.page_sections?.[page] || [];
  const setSections = (next) => setC((cur) => ({ ...cur, page_sections: { ...cur.page_sections, [page]: next } }));
  const moveSection = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= sections.length) return;
    const next = [...sections];
    [next[i], next[j]] = [next[j], next[i]];
    setSections(next);
  };

  if (!c) return <div className="min-h-screen bg-[#F4F7FA] grid place-items-center text-sm text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid="website-page">
      <AdminHeader title="Website" testid="website-title" maxW="max-w-3xl" actions={(
        <>
          <a href="/" target="_blank" rel="noreferrer" data-testid="cms-view-site"
            className="h-11 px-4 rounded-lg border border-slate-300 text-[#005EB8] hover:border-[#005EB8] text-sm font-bold inline-flex items-center gap-1.5 bg-white press">
            View website
          </a>
          <button onClick={() => { setC(normalizeSiteContent({})); clearSiteContentCache(); toast.message("Defaults loaded — press Save to publish"); }} data-testid="cms-reset"
            className="h-11 px-4 rounded-lg border border-slate-300 text-slate-600 hover:border-slate-400 text-sm font-bold inline-flex items-center gap-1.5 bg-white press">
            <RotateCcw className="h-4 w-4" /> Reset
          </button>
          <button onClick={save} disabled={saving} data-testid="cms-save"
            className="h-11 px-5 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-50 text-white text-sm font-bold inline-flex items-center gap-2 press">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save & publish"}
          </button>
        </>
      )} />

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-5 pb-24">
        <div className="flex gap-2 flex-wrap" data-testid="cms-tabs">
          {TABS.map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`cms-tab-${k}`}
              className={`h-10 px-4 rounded-full text-sm font-bold transition-colors ${tab === k ? "bg-[#005EB8] text-white" : "bg-white border border-slate-300 text-slate-600 hover:border-[#005EB8]"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "pages" && (
          <>
            <p className="text-xs text-slate-500">Pick a page, then edit each section — text, photos, buttons. Add new sections, reorder them with the arrows, or delete them. Nothing changes until you press Save & publish.</p>
            <div className="flex gap-2 flex-wrap" data-testid="cms-pages-picker">
              {EDITABLE_PAGES.map(([k, label]) => (
                <button key={k} onClick={() => setPage(k)} data-testid={`cms-page-${k}`}
                  className={`h-9 px-3 rounded-lg text-xs font-bold transition-colors ${page === k ? "bg-[#0F2547] text-white" : "bg-white border border-slate-300 text-slate-600 hover:border-slate-400"}`}>
                  {label}
                </button>
              ))}
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3" data-testid="cms-page-meta">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Google listing for this page (SEO)</p>
              <Field label="Page title (browser tab &amp; Google headline)">
                <input className={inputCls} value={(c.page_meta?.[page]?.title) || ""} placeholder="Leave blank to use the built-in title"
                  onChange={(e) => setC((cur) => ({ ...cur, page_meta: { ...(cur.page_meta || {}), [page]: { ...((cur.page_meta || {})[page] || {}), title: e.target.value } } }))}
                  data-testid="cms-page-meta-title" />
              </Field>
              <Field label="Page description (the lines under the Google headline)">
                <textarea rows={2} className={areaCls} value={(c.page_meta?.[page]?.description) || ""} placeholder="Leave blank to use the built-in description"
                  onChange={(e) => setC((cur) => ({ ...cur, page_meta: { ...(cur.page_meta || {}), [page]: { ...((cur.page_meta || {})[page] || {}), description: e.target.value } } }))}
                  data-testid="cms-page-meta-description" />
              </Field>
              <p className="text-[11px] text-slate-400">Changes go live when you press Save &amp; publish above. The blog's name and intro are edited under the Blog tab → Blog page settings.</p>
            </div>
            <div className="space-y-3">
              {sections.map((s, i) => (
                <SectionEditor key={s.id || i} s={s} index={i} total={sections.length}
                  onChange={(next) => setSections(sections.map((x, j) => (j === i ? next : x)))}
                  onMove={(dir) => moveSection(i, dir)}
                  onDelete={() => { if (window.confirm(`Delete the "${SECTION_TYPES[s.type] || s.type}" section? It only goes live after you press Save & publish.`)) setSections(sections.filter((_, j) => j !== i)); }} />
              ))}
            </div>
            <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-4 flex flex-wrap items-center gap-2" data-testid="cms-add-section">
              <select className="h-11 rounded-lg border border-slate-300 px-3 text-sm bg-white flex-1 min-w-[180px]" value={addType} onChange={(e) => setAddType(e.target.value)} data-testid="cms-add-type">
                {Object.entries(SECTION_TYPES).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
              <button onClick={() => setSections([...sections, newSection(addType)])} data-testid="cms-add-btn"
                className="h-11 px-4 rounded-lg bg-[#005EB8] hover:bg-[#004a93] text-white text-sm font-bold inline-flex items-center gap-1.5">
                <Plus className="h-4 w-4" /> Add section
              </button>
            </div>
          </>
        )}

        {tab === "services" && (
          <>
            <p className="text-xs text-slate-500">These services appear in the services grids across the site. Each service also has its own page — edit that under the Pages tab.</p>
            {c.services_catalog.map((svc, i) => {
              const updSvc = (k, v) => setC((cur) => ({ ...cur, services_catalog: cur.services_catalog.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
              return (
                <section key={svc.slug || i} className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid={`cms-service-${i}`}>
                  <div className="flex items-center gap-2">
                    <h2 className="font-heading text-lg font-bold text-[#0F2547]">{svc.name || `Service ${i + 1}`}</h2>
                    <span className="text-[10px] text-slate-400 font-mono">/services/{svc.slug}</span>
                    <button onClick={() => setC((cur) => ({ ...cur, services_catalog: cur.services_catalog.filter((_, j) => j !== i) }))}
                      className="ml-auto text-xs text-[#D5281B] font-bold" data-testid={`cms-service-del-${i}`}>Remove</button>
                  </div>
                  <Field label="Service name"><input className={inputCls} value={svc.name || ""} onChange={(e) => updSvc("name", e.target.value)} data-testid={`cms-service-name-${i}`} /></Field>
                  <Field label="Icon">
                    <select className={inputCls} value={svc.icon || "heart"} onChange={(e) => updSvc("icon", e.target.value)} data-testid={`cms-service-icon-${i}`}>
                      {Object.keys(ICONS).map((k) => <option key={k} value={k}>{k}</option>)}
                    </select>
                  </Field>
                  <Field label="Short intro (shown on cards)"><textarea rows={2} className={areaCls} value={svc.tagline || ""} onChange={(e) => updSvc("tagline", e.target.value)} data-testid={`cms-service-tagline-${i}`} /></Field>
                  <Field label="Longer description"><textarea rows={3} className={areaCls} value={svc.description || ""} onChange={(e) => updSvc("description", e.target.value)} data-testid={`cms-service-desc-${i}`} /></Field>
                  <ImageField value={svc.image || ""} onChange={(v) => updSvc("image", v)} testid={`cms-service-image-${i}`} label="Photo" />
                  <StringListEditor items={svc.points || []} onChange={(v) => updSvc("points", v)} testid={`cms-service-points-${i}`} label="Tick-list points" />
                </section>
              );
            })}
            <button onClick={() => setC((cur) => ({ ...cur, services_catalog: [...cur.services_catalog, { slug: `service-${Date.now().toString(36)}`, icon: "heart", name: "New service", tagline: "", description: "", image: "", points: [] }] }))}
              className="h-11 px-4 rounded-lg border border-slate-300 text-[#005EB8] hover:border-[#005EB8] text-sm font-bold inline-flex items-center gap-1.5 bg-white" data-testid="cms-service-add">
              <Plus className="h-4 w-4" /> Add a service
            </button>

            <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="cms-calculator">
              <h2 className="font-heading text-lg font-bold text-[#0F2547]">Staff calculator page text</h2>
              <p className="text-xs text-slate-500">The scoring questions and clinical tables follow the HSE Purple Guide and stay fixed — everything around them is editable here.</p>
              <ImageField value={c.calculator_texts.hero_image || ""} onChange={(v) => setC((cur) => ({ ...cur, calculator_texts: { ...cur.calculator_texts, hero_image: v } }))} testid="cms-calc-image" label="Banner photo" />
              {CALC_FIELDS.map(([k, label, long]) => (
                <Field key={k} label={label}>
                  {long ? (
                    <textarea rows={3} className={areaCls} value={c.calculator_texts[k] || ""} onChange={(e) => setC((cur) => ({ ...cur, calculator_texts: { ...cur.calculator_texts, [k]: e.target.value } }))} data-testid={`cms-calc-${k}`} />
                  ) : (
                    <input className={inputCls} value={c.calculator_texts[k] || ""} onChange={(e) => setC((cur) => ({ ...cur, calculator_texts: { ...cur.calculator_texts, [k]: e.target.value } }))} data-testid={`cms-calc-${k}`} />
                  )}
                </Field>
              ))}
            </section>
          </>
        )}

        {tab === "blog" && <BlogManager />}

        {tab === "settings" && (
          <>
            <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="cms-contact">
              <h2 className="font-heading text-lg font-bold text-[#0F2547]">Contact details</h2>
              <p className="text-xs text-slate-500">Shown on the Contact page and in the footer of every page.</p>
              <Field label="Email address"><input className={inputCls} value={c.contact_info.email || ""} onChange={(e) => setC((cur) => ({ ...cur, contact_info: { ...cur.contact_info, email: e.target.value } }))} data-testid="cms-contact-email" /></Field>
              <Field label="Phone line"><input className={inputCls} value={c.contact_info.phone || ""} onChange={(e) => setC((cur) => ({ ...cur, contact_info: { ...cur.contact_info, phone: e.target.value } }))} data-testid="cms-contact-phone" /></Field>
              <Field label="Location line"><input className={inputCls} value={c.contact_info.location || ""} onChange={(e) => setC((cur) => ({ ...cur, contact_info: { ...cur.contact_info, location: e.target.value } }))} data-testid="cms-contact-location" /></Field>
            </section>

            <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="cms-header">
              <h2 className="font-heading text-lg font-bold text-[#0F2547]">Header (top of every page)</h2>
              <ImageField value={c.header.logo || ""} onChange={(v) => setC((cur) => ({ ...cur, header: { ...cur.header, logo: v } }))} testid="cms-header-logo" label="Logo (remove to use the default)" />
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Quote button text"><input className={inputCls} value={c.header.cta_label || ""} onChange={(e) => setC((cur) => ({ ...cur, header: { ...cur.header, cta_label: e.target.value } }))} data-testid="cms-header-cta" /></Field>
                <Field label="Quote button links to"><input className={inputCls} value={c.header.cta_to || ""} onChange={(e) => setC((cur) => ({ ...cur, header: { ...cur.header, cta_to: e.target.value } }))} data-testid="cms-header-cta-to" /></Field>
              </div>
            </section>

            <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="cms-nav">
              <h2 className="font-heading text-lg font-bold text-[#0F2547]">Menu links</h2>
              <p className="text-xs text-slate-500">The links along the top of the website — rename them, repoint them, reorder with the arrows, switch them off, or add your own. The footer's Explore list follows these too. The Services item automatically lists your services in a dropdown.</p>
              <div className="space-y-2">
                {c.nav_links.map((n, i) => {
                  const updNav = (k, v) => setC((cur) => ({ ...cur, nav_links: cur.nav_links.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
                  const moveNav = (dir) => {
                    const j = i + dir;
                    if (j < 0 || j >= c.nav_links.length) return;
                    setC((cur) => {
                      const arr = [...cur.nav_links];
                      [arr[i], arr[j]] = [arr[j], arr[i]];
                      return { ...cur, nav_links: arr };
                    });
                  };
                  return (
                    <div key={i} className="flex items-center gap-2" data-testid={`cms-nav-${i}`}>
                      <input type="checkbox" checked={n.on !== false} onChange={(e) => updNav("on", e.target.checked)} className="h-4 w-4 accent-[#005EB8] shrink-0" data-testid={`cms-nav-on-${i}`} aria-label="Show link" />
                      <input className={inputCls} value={n.label || ""} placeholder="Label" onChange={(e) => updNav("label", e.target.value)} data-testid={`cms-nav-label-${i}`} />
                      <input className={inputCls} value={n.to || ""} placeholder="Links to (e.g. /faq)" onChange={(e) => updNav("to", e.target.value)} data-testid={`cms-nav-to-${i}`} />
                      <button onClick={() => moveNav(-1)} disabled={i === 0} aria-label="Move up" className="h-9 w-9 grid place-items-center rounded-lg border border-slate-200 text-slate-500 disabled:opacity-30 shrink-0" data-testid={`cms-nav-up-${i}`}><ArrowUp className="h-4 w-4" /></button>
                      <button onClick={() => moveNav(1)} disabled={i === c.nav_links.length - 1} aria-label="Move down" className="h-9 w-9 grid place-items-center rounded-lg border border-slate-200 text-slate-500 disabled:opacity-30 shrink-0" data-testid={`cms-nav-down-${i}`}><ArrowDown className="h-4 w-4" /></button>
                      <button onClick={() => setC((cur) => ({ ...cur, nav_links: cur.nav_links.filter((_, j) => j !== i) }))} aria-label="Remove link" className="h-9 w-9 grid place-items-center rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50 shrink-0" data-testid={`cms-nav-del-${i}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  );
                })}
              </div>
              <button onClick={() => setC((cur) => ({ ...cur, nav_links: [...cur.nav_links, { label: "New link", to: "/", page: null, on: true }] }))}
                className="h-10 px-3 rounded-lg border border-slate-300 text-[#005EB8] hover:border-[#005EB8] text-xs font-bold inline-flex items-center gap-1.5" data-testid="cms-nav-add">
                <Plus className="h-3.5 w-3.5" /> Add link
              </button>
            </section>

            <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="cms-footer">
              <h2 className="font-heading text-lg font-bold text-[#0F2547]">Footer (bottom of every page)</h2>
              <Field label="Tagline under the logo"><textarea rows={2} className={areaCls} value={c.footer.tagline || ""} onChange={(e) => setC((cur) => ({ ...cur, footer: { ...cur.footer, tagline: e.target.value } }))} data-testid="cms-footer-tagline" /></Field>
              <Field label="Bottom line (next to the copyright)"><input className={inputCls} value={c.footer.bottom_note || ""} onChange={(e) => setC((cur) => ({ ...cur, footer: { ...cur.footer, bottom_note: e.target.value } }))} data-testid="cms-footer-note" /></Field>
              <p className="text-xs text-slate-500">The footer's contact details come from the Contact details card above, and its Explore list follows your menu links.</p>
            </section>

            <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="cms-blog-settings">
              <h2 className="font-heading text-lg font-bold text-[#0F2547]">Blog</h2>
              <label className="flex items-center gap-2.5 h-11 px-3 rounded-lg border border-slate-200 hover:border-slate-300 cursor-pointer" data-testid="cms-blog-enabled">
                <input type="checkbox" checked={c.blog_settings.enabled !== false} onChange={(e) => setC((cur) => ({ ...cur, blog_settings: { ...cur.blog_settings, enabled: e.target.checked } }))} className="h-4 w-4 accent-[#005EB8]" />
                <span className="text-sm font-semibold text-slate-700">Show the Blog link in the website menu</span>
              </label>
              <Field label="Blog page title"><input className={inputCls} value={c.blog_settings.title || ""} onChange={(e) => setC((cur) => ({ ...cur, blog_settings: { ...cur.blog_settings, title: e.target.value } }))} data-testid="cms-blog-title" /></Field>
              <Field label="Blog page intro"><textarea rows={2} className={areaCls} value={c.blog_settings.subtitle || ""} onChange={(e) => setC((cur) => ({ ...cur, blog_settings: { ...cur.blog_settings, subtitle: e.target.value } }))} data-testid="cms-blog-subtitle" /></Field>
              <ImageField value={c.blog_settings.image || ""} onChange={(v) => setC((cur) => ({ ...cur, blog_settings: { ...cur.blog_settings, image: v } }))} testid="cms-blog-image" label="Blog banner photo" />
            </section>

            <SocialCard />

            <VisitorsCard />

            <SeoCard />

            <section className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="cms-pages">
              <h2 className="font-heading text-lg font-bold text-[#0F2547]">Pages in the menu</h2>
              <p className="text-xs text-slate-500 mb-3">Untick to remove a page from the website menu.</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {Object.entries(PAGE_LABELS).map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2.5 h-11 px-3 rounded-lg border border-slate-200 hover:border-slate-300 cursor-pointer" data-testid={`cms-pagetoggle-${k}`}>
                    <input type="checkbox" checked={!!c.pages[k]} onChange={(e) => setC((cur) => ({ ...cur, pages: { ...cur.pages, [k]: e.target.checked } }))}
                      className="h-4 w-4 accent-[#005EB8]" />
                    <span className="text-sm font-semibold text-slate-700">{label}</span>
                  </label>
                ))}
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
